import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import { reportError } from '../../lib/errors';

interface PortalCustomer {
    id: string;
    name: string;
    email: string;
}

interface PortalOrg {
    id: string;
    slug: string;
    name: string;
    logo_url?: string;
    accent_color?: string;
}

interface PortalInvoice {
    id: string;
    date: string;
    invoice_number: string;
    total_gross: number;
    vat_amount: number;
    status: 'draft' | 'pending' | 'posted' | 'paid';
    description: string;
    job_number?: string;
    vehicle_reg?: string;
}

interface PortalContextType {
    customer: PortalCustomer | null;
    org: PortalOrg | null;
    invoices: PortalInvoice[];
    loading: boolean;
    error: string | null;
    isValid: boolean;
    targetedInvoiceId: string | null;
    refreshInvoices: () => Promise<void>;
}

const PortalContext = createContext<PortalContextType | null>(null);

export const usePortal = () => {
    const ctx = useContext(PortalContext);
    if (!ctx) throw new Error('usePortal must be used within PortalProvider');
    return ctx;
};

interface PortalProviderProps {
    token: string;
    children: ReactNode;
}

export const PortalProvider = ({ token, children }: PortalProviderProps) => {
    const [customer, setCustomer] = useState<PortalCustomer | null>(null);
    const [org, setOrg] = useState<PortalOrg | null>(null);
    const [invoices, setInvoices] = useState<PortalInvoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isValid, setIsValid] = useState(false);
    const [targetedInvoiceId, setTargetedInvoiceId] = useState<string | null>(null);

    const validateAndFetch = async () => {
        setLoading(true);
        setError(null);

        try {
            // Validate token via RPC
            const { data: validation, error: valError } = await supabase
                .rpc('fn_validate_portal_token', { p_token: token });

            if (valError || !validation?.valid) {
                setError(validation?.error || 'Invalid or expired link');
                setIsValid(false);
                setLoading(false);
                return;
            }

            setIsValid(true);
            setTargetedInvoiceId(validation.invoice_id);
            setCustomer({
                id: validation.customer_id,
                name: validation.customer_name,
                email: validation.customer_email
            });

            // Branding comes back with the token validation: a portal visitor
            // has no Supabase session, and anon cannot read organizations.
            setOrg({
                id: validation.org_id,
                slug: validation.org_slug,
                name: validation.org_name,
                logo_url: validation.org_logo_url ?? undefined,
                accent_color: validation.org_accent_color ?? undefined,
            });

            // Fetch invoices
            // Token-gated: returns only this token's customer's issued invoices.
            const { data: invoiceData, error: invoiceDataError } = await supabase
                .rpc('fn_get_portal_invoices', { p_token: token });
            if (invoiceDataError) reportError(invoiceDataError, 'portal-context.get-portal-invoices');

            setInvoices(invoiceData || []);

        } catch (err) {
            setError('Something went wrong. Please try again.');
            console.error('Portal validation error:', err);
        }

        setLoading(false);
    };

    const refreshInvoices = async () => {
        if (!customer) return;
        const { data, error: fetchError } = await supabase
            .rpc('fn_get_portal_invoices', { p_token: token });
        if (fetchError) reportError(fetchError, 'portal-context.get-portal-invoices');
        setInvoices(data || []);
    };

    useEffect(() => {
        if (token) validateAndFetch();
    }, [token]);

    return (
        <PortalContext.Provider value={{
            customer,
            org,
            invoices,
            loading,
            error,
            isValid,
            targetedInvoiceId,
            refreshInvoices
        }}>
            {children}
        </PortalContext.Provider>
    );
};
